import { useState } from "react";
import { Pencil, Trash } from "react-bootstrap-icons";
import { useListsContext } from "../ListsContext";
import {
  DeleteIconButton,
  EditField,
  EditFields,
  EditLabel,
  IconButton,
  ItemBody,
  ItemNote,
  ItemRow,
  ItemText,
  QuantityBadge,
  RowActions,
  SmallButton,
  StagePill,
  StagePills,
  TextInput,
} from "../Lists.styled";
import { ConfirmModal } from "./ConfirmModal";
import { ListItem } from "../types";

// text · qty badge · note · done pill, with edit/delete icons alongside and edit
// expanding inline fields. The whole row is the drag target, wired up by ListItems.
export function ListItemRow(props: {
  item: ListItem;
  dragging?: boolean;
  // Supplied by SortableRow. Absent for the floating copy, which does not drag itself.
  rowRef?: (node: HTMLElement | null) => void;
  rowStyle?: React.CSSProperties;
  rowHandlers?: Record<string, unknown>;
}) {
  const { item } = props;
  const { toggleItem, updateItem, removeItem } = useListsContext();
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [text, setText] = useState(item.Text ?? "");
  const [quantity, setQuantity] = useState(item.Quantity ?? "");
  const [note, setNote] = useState(item.Note ?? "");

  function startEditing() {
    setText(item.Text ?? "");
    setQuantity(item.Quantity ?? "");
    setNote(item.Note ?? "");
    setEditing(true);
  }

  // A checked item has served its purpose, so deleting it needs no ceremony. An
  // unchecked one is still wanted, so deleting it is probably a mis-tap and asks first.
  function handleDelete() {
    if (item.Checked) {
      removeItem(item.ListId, item.ItemId);
      return;
    }
    setDeleteOpen(true);
  }

  function save() {
    if (text.trim() === "") {
      return;
    }
    updateItem(item.ListId, item.ItemId, {
      Text: text,
      Quantity: quantity,
      Note: note,
    });
    setEditing(false);
  }

  if (editing) {
    return (
      <ItemRow ref={props.rowRef} style={props.rowStyle}>
        <ItemBody>
          <EditFields>
            {/* Ids are suffixed with the item's own id so every row's labels point at
                that row's own inputs. */}
            <EditField>
              <EditLabel htmlFor={`item-text-${item.ItemId}`}>Item</EditLabel>
              <TextInput
                id={`item-text-${item.ItemId}`}
                autoFocus
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            </EditField>
            <EditField>
              <EditLabel htmlFor={`item-qty-${item.ItemId}`}>Quantity</EditLabel>
              <TextInput
                id={`item-qty-${item.ItemId}`}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </EditField>
            <EditField>
              <EditLabel htmlFor={`item-note-${item.ItemId}`}>Note</EditLabel>
              <TextInput
                id={`item-note-${item.ItemId}`}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </EditField>
            <RowActions>
              <SmallButton onClick={save}>Save</SmallButton>
              <SmallButton onClick={() => setEditing(false)}>Cancel</SmallButton>
            </RowActions>
          </EditFields>
        </ItemBody>
      </ItemRow>
    );
  }

  return (
    <ItemRow
      ref={props.rowRef}
      style={props.rowStyle}
      {...props.rowHandlers}
      $dragging={props.dragging}
    >
      <ItemBody>
        <ItemText $checked={item.Checked}>
          {item.Text}
          {item.Quantity ? <QuantityBadge>{item.Quantity}</QuantityBadge> : null}
        </ItemText>
        {item.Note ? <ItemNote>{item.Note}</ItemNote> : null}
        {/* One pill for now. Lists will later carry their own named stages, and this
            row will render one pill per stage. */}
        <StagePills $spaced>
          <StagePill
            type="button"
            $on={item.Checked}
            aria-pressed={item.Checked}
            aria-label={`Done: ${item.Text}`}
            onClick={() => toggleItem(item.ListId, item.ItemId)}
          >
            Done
          </StagePill>
        </StagePills>
      </ItemBody>
      <RowActions>
        {/* Same pencil and trash can as the list controls above, so edit and delete
            read the same whether they act on a list or on one item. */}
        <IconButton
          type="button"
          title={`Edit "${item.Text}"`}
          aria-label={`Edit "${item.Text}"`}
          onClick={startEditing}
        >
          <Pencil />
        </IconButton>
        <DeleteIconButton
          type="button"
          title={`Delete "${item.Text}"`}
          aria-label={`Delete "${item.Text}"`}
          onClick={handleDelete}
        >
          <Trash />
        </DeleteIconButton>
      </RowActions>
      {deleteOpen && (
        <ConfirmModal
          heading="Delete Item"
          message={`"${item.Text}" hasn't been checked off yet. Delete it anyway?`}
          confirmLabel="Delete Item"
          onConfirm={() => removeItem(item.ListId, item.ItemId)}
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </ItemRow>
  );
}
